// TODO: Refactor and extend form configuration model

import { FormConfigProperties } from './properties/form-config-properties.model';
import { FormSection } from './form-section.model';
import { WidgetBuilder } from './form-widget-builder.model';

export interface FormScribeInfo {
  schema_version: 1;
  wizard_session_id: string;
  wizard_step_index?: number;
  scribe_session_id?: string;
  scribe_job_id?: string | null;
}

export class FormConfig {
  public properties: FormConfigProperties;
  public sections: FormSection[];
  public version: number[];
  public pageFeId: string;
  public scribe_info: FormScribeInfo | null;

  constructor(data: any = {}, projectName = '') {
    // RESERVED: Form Data Minify and Restore
    // const formDictionary = this.createDynamicDictionary(data);
    // const formMinified = this.minifyJson(data, formDictionary);
    // const formRestored = this.restoreJson(formMinified, formDictionary);

    this.version = data.version || [1];
    this.properties = new FormConfigProperties(data.properties);

    if (data.sections instanceof Array) {
      const sections = data.sections.map((item) => {
        return new FormSection(item);
      });
      this.sections = sections;
    } else {
      this.sections = [new FormSection(data.section)];
    }
    this.pageFeId = data.pageFeId || 'uid' + Math.random().toString(36).substring(2) + Date.now();
    this.scribe_info = this.parseScribeInfo(data.scribe_info);
  }

  private parseScribeInfo(value: unknown): FormScribeInfo | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return null;
    }

    const info = value as Record<string, unknown>;
    const wizardSessionId = info['wizard_session_id'];
    const wizardStepIndex = info['wizard_step_index'];
    const hasScribeSessionId = Object.prototype.hasOwnProperty.call(info, 'scribe_session_id');
    const hasScribeJobId = Object.prototype.hasOwnProperty.call(info, 'scribe_job_id');
    const scribeSessionId = info['scribe_session_id'];
    const scribeJobId = info['scribe_job_id'];

    const normalizedScribeSessionId =
      typeof scribeSessionId === 'string' && scribeSessionId.trim() ? scribeSessionId.trim() : null;
    const normalizedScribeJobId =
      typeof scribeJobId === 'string' && scribeJobId.trim() ? scribeJobId.trim() : null;
    const hasValidWizardStepIndex =
      typeof wizardStepIndex === 'number' &&
      Number.isInteger(wizardStepIndex) &&
      wizardStepIndex >= 0;
    const hasDirectScribeId = !!normalizedScribeSessionId || !!normalizedScribeJobId;

    if (
      info['schema_version'] !== 1 ||
      typeof wizardSessionId !== 'string' ||
      !wizardSessionId.trim() ||
      (!hasValidWizardStepIndex && !hasDirectScribeId) ||
      (hasScribeSessionId && !normalizedScribeSessionId) ||
      (hasScribeJobId && scribeJobId !== null && !normalizedScribeJobId)
    ) {
      return null;
    }

    return {
      schema_version: 1,
      wizard_session_id: wizardSessionId,
      ...(hasValidWizardStepIndex ? { wizard_step_index: wizardStepIndex } : {}),
      ...(normalizedScribeSessionId ? { scribe_session_id: normalizedScribeSessionId } : {}),
      ...(hasScribeJobId ? { scribe_job_id: normalizedScribeJobId } : {}),
    };
  }

  isValid() {
    const invalidWidgets: any = [];
    this.sections.forEach((section) => {
      section.widgets.forEach((widget) => {
        const clonedWidget = WidgetBuilder.create(widget);
        if (!clonedWidget.isValid()) {
          invalidWidgets.push(clonedWidget);
        }
      });
    });
    return invalidWidgets.length === 0;
  }

  hasWidgets() {
    let totalWidgets = 0;

    if (!this.sections || this.sections.length === 0) {
      return false;
    }

    this.sections.forEach((section) => {
      totalWidgets += section.widgets.length;
    });

    return totalWidgets > 0;
  }

  getAllMediaIds() {
    const mediaIds: any = [];
    this.sections.forEach((section) => {
      section.widgets.forEach((widget) => {
        const clonedWidget = WidgetBuilder.create(widget);
        if (clonedWidget.name === 'fb-media-gallery') {
          if (clonedWidget.properties.value && clonedWidget.properties.value.value) {
            clonedWidget.properties.value.value.forEach((v) => {
              mediaIds.push(v.value.value);
            });
          }
        }
      });
    });
    return mediaIds;
  }

  getAllVideoIds() {
    const videoIds: any = [];
    this.sections.forEach((section) => {
      section.widgets.forEach((widget) => {
        const clonedWidget = WidgetBuilder.create(widget);
        if (clonedWidget.name === 'fb-media-gallery') {
          if (clonedWidget.properties.value && clonedWidget.properties.value.value) {
            clonedWidget.properties.value.value.forEach((v) => {
              if (v.value.type === 'video') {
                videoIds.push(v.value.value);
              }
            });
          }
        }
      });
    });
    return videoIds;
  }

  //#region Form Config Minifier
  createDynamicDictionary(json: any): { [key: string]: string } {
    const dictionary: { [key: string]: string } = {};
    const keys: Set<string> = new Set();

    function collectKeys(obj: any) {
      if (typeof obj !== 'object' || obj === null) {
        return;
      }

      if (Array.isArray(obj)) {
        obj.forEach(collectKeys);
        return;
      }

      for (const key in obj) {
        keys.add(key);
        if (typeof obj[key] === 'object' && obj[key] !== null) {
          collectKeys(obj[key]);
        }
      }
    }

    function generateKey(keyIndex: number): string {
      let key = '';
      let currentKeyIndex = keyIndex;
      do {
        const charCode = 'a'.charCodeAt(0) + (currentKeyIndex % 26);
        key = String.fromCharCode(charCode) + key;
        currentKeyIndex = Math.floor(currentKeyIndex / 26) - 1;
      } while (currentKeyIndex >= 0);
      return key;
    }

    collectKeys(json);

    const keyArray = Array.from(keys);

    for (let i = 0; i < keyArray.length; i++) {
      dictionary[keyArray[i]] = generateKey(i);
    }

    return dictionary;
  }

  minifyJson(json: any, dictionary: { [key: string]: string }): any {
    if (typeof json !== 'object' || json === null) {
      return json;
    }

    if (Array.isArray(json)) {
      return json.map((item) => this.minifyJson(item, dictionary));
    }

    const minifiedJson: { [key: string]: any } = {};
    for (const key in json) {
      const minifiedKey = dictionary[key];
      minifiedJson[minifiedKey] = this.minifyJson(json[key], dictionary);
    }

    return minifiedJson;
  }

  restoreJson(minifiedJson: any, dictionary: { [key: string]: string }): any {
    if (typeof minifiedJson !== 'object' || minifiedJson === null) {
      return minifiedJson;
    }

    if (Array.isArray(minifiedJson)) {
      return minifiedJson.map((item) => this.restoreJson(item, dictionary));
    }

    const originalJson: { [key: string]: any } = {};
    for (const minifiedKey in minifiedJson) {
      const originalKey = Object.keys(dictionary).find((key) => dictionary[key] === minifiedKey);
      if (originalKey) {
        originalJson[originalKey] = this.restoreJson(minifiedJson[minifiedKey], dictionary);
      }
    }

    return originalJson;
  }

  //#endregion
}
